import { useState } from 'react';
import styled from '@emotion/styled';
import {
  Skeleton,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
import {
  getSplatUserType,
  FUSION_CLOSER,
  DIRECT_TO_HOME,
  isSunrunManaged,
} from '@sunrun/sales-experience-shared';
import { useFeatureFlagEnabled } from '@sunrun/sales-experience-shared/react-feature-flags';
import { ReportPoachingModal } from 'components/molecules/ReportPoachingModal';
import { publish, publishMessage } from 'helpers/messaging';
import { getRecheckUrl } from 'helpers/reCheckURL';
import { getUrls } from 'helpers/getUrlHelper';
import { isStreetOrFusionHelper } from 'helpers/repTypeHelper';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';
import { useUserProfile } from 'providers/UserProfile';

const theRunThroughUrls = {
  [FUSION_CLOSER]:
    'https://current.sunrun.com/site/b7862463-82a0-531a-b264-209806cbe90f/dashboard',
  [DIRECT_TO_HOME]:
    'https://current.sunrun.com/site/ba74dc6e-b8c4-5742-9b86-5bb349771a9f/dashboard',
};

const NAVY = tokens.BRAND_HEROBLUE;

type LinkPillProps = {
  label: string;
  icon: SvgNames;
  onClick: () => void;
  testId?: string;
};

const LinkPill = ({ label, icon, onClick, testId }: LinkPillProps) => (
  <PillButton type="button" data-testid={testId} onClick={onClick}>
    <StyledIcon name={icon} height={18} width={18} color={NAVY} />
    <PillLabel variant="span" size="small">
      {label}
    </PillLabel>
  </PillButton>
);

const HelpfulLinks = () => {
  const { userProfile } = useUserProfile();
  const { oneBaseUrl } = getUrls();
  const academyPageEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ACADEMY_PAGE);
  const [isReportPoachingOpen, setIsReportPoachingOpen] = useState(false);
  const splatUserType = userProfile ? getSplatUserType(userProfile) : undefined;
  const isD2H = splatUserType === 'Direct to Home';
  const showRunX = userProfile ? isSunrunManaged(userProfile) : false;
  const isStreetOrFusion = userProfile
    ? isStreetOrFusionHelper(userProfile)
    : false;

  const open = (url: string) => {
    window.open(url, '_blank');
  };

  const openAcademy = () => {
    if (showRunX && academyPageEnabled) {
      publishMessage({
        source: 'Dashboard',
        type: 'COMMAND:CHANGE_URL',
        payload: {
          url: `${oneBaseUrl}/academy`,
        },
      });
      return;
    }

    open('https://sunrunx.com/');
  };

  return (
    <Section>
      <ScrollTrack role="toolbar" aria-label="Helpful links">
        <LinkPill
          label="Create lead"
          icon={SvgNames.Plus}
          onClick={() => publish.createLead()}
        />
        <LinkPill
          label="Report poaching"
          icon={SvgNames.ArrowSquareOutSvg}
          testId="report-poaching-button"
          onClick={() => setIsReportPoachingOpen(true)}
        />
        <LinkPill
          label="View Pay"
          icon={SvgNames.ArrowSquareOutSvg}
          onClick={() =>
            open(
              'https://10ay.online.tableau.com/#/site/sunrunbi/views/CommissionTracker/CommissionTracker?:iid=1',
            )
          }
        />
        <LinkPill
          label="Sunrun Academy"
          icon={SvgNames.ArrowSquareOutSvg}
          onClick={openAcademy}
        />
        <LinkPill
          label="Resource Center"
          icon={SvgNames.ArrowSquareOutSvg}
          onClick={() => open('https://sunrun.docebosaas.com/')}
        />
        {userProfile && (
          <LinkPill
            label="Recheck"
            icon={SvgNames.ArrowSquareOutSvg}
            testId="recheck-button"
            onClick={() => open(getRecheckUrl(userProfile))}
          />
        )}
        <LinkPill
          label="My Apps"
          icon={SvgNames.Grid}
          onClick={() => open('https://sunrun.okta.com/app/UserHome')}
        />
        <LinkPill
          label="Current"
          icon={SvgNames.ArrowSquareOutSvg}
          onClick={() => open('http://current.sunrun.com/')}
        />
        <LinkPill
          label="Company News"
          icon={SvgNames.ArrowSquareOutSvg}
          onClick={() =>
            open(
              'https://current.sunrun.com/site/7b6848bd-8bfb-5e2e-b56d-41b94260487d/content?type=page&pageCategoryId=5daf3824-a623-53e2-a6e8-06a6ec0120a0',
            )
          }
        />
        {!userProfile ? (
          <SkeletonPill />
        ) : (
          isD2H && (
            <LinkPill
              label="Open Terros"
              icon={SvgNames.ArrowSquareOutSvg}
              onClick={() => publish.openTerros()}
            />
          )
        )}
        {isStreetOrFusion && (
          <LinkPill
            label="The Run Through"
            icon={SvgNames.ArrowSquareOutSvg}
            onClick={() =>
              open(
                theRunThroughUrls[
                  splatUserType as keyof typeof theRunThroughUrls
                ],
              )
            }
          />
        )}
        {!userProfile ? (
          <SkeletonPill />
        ) : (
          showRunX && (
            <LinkPill
              label="Shop RunX"
              icon={SvgNames.ArrowSquareOutSvg}
              onClick={() => open('https://shoprunx.sunrun.com/')}
            />
          )
        )}
      </ScrollTrack>
      {isReportPoachingOpen && (
        <ReportPoachingModal onClose={() => setIsReportPoachingOpen(false)} />
      )}
    </Section>
  );
};

const Section = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: #f8f5f0;
  border-radius: 0 0 8px 8px;
`;

const ScrollTrack = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
  padding: 12px 16px 16px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${tokens.TINTS_OFF_WHITE_20};
    border-radius: 3px;
  }
`;

const PillButton = styled.button`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 10px 16px;
  margin: 0;
  border: 1px solid ${NAVY};
  border-radius: 9999px;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: ${NAVY};
  white-space: nowrap;

  &:hover {
    background-color: rgba(31, 38, 71, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${NAVY};
    outline-offset: 2px;
  }
`;

const PillLabel = styled(Typography)`
  color: ${NAVY};
  line-height: 1.2;
`;

const StyledIcon = styled(SvgIcon)`
  flex-shrink: 0;
`;

const SkeletonPill = styled(Skeleton)`
  flex-shrink: 0;
  width: 112px;
  height: 40px;
  border-radius: 9999px;
`;

export { HelpfulLinks };
